/* ══════════════════════════════════════════════════════════════════════════
 * ZARYNA'S ANSWER, ON THE SCREEN IT WAS ASKED FROM — Al-Noor, 11 Oct 2026:
 * "Zaryna should be everywhere. Signed in or not. The microphone, her
 * spinning, everything… if you're asking from the explore bar, you come back
 * to the explore bar. If you're asking from the homepage, you come back to the
 * homepage."
 *
 *   useZarynaAnswer(question, { market, near, source })
 *     asks her once per question (never per keystroke — each ask is a paid
 *     model call), streaming; signed in or a visitor (the server counts a
 *     visitor's questions per phone, 189). Gives { state, text, found, cards }
 *     and stop().
 *   <ZarynaAnswer>   her Z turning with her steps — the question back to them
 *     ("…near you"), what she found, "Picking…" — then her words as they
 *     arrive, then her cards when `withCards` (Home; Explore has its list).
 *
 * One piece for Home and Explore, so the two screens cannot answer differently.
 * ⚠️ A FAILURE DRAWS NOTHING — the screen under it still works.
 * ══════════════════════════════════════════════════════════════════════════ */
import React, { useEffect, useRef, useState } from 'react';
import { call, askStreaming } from './api.js';
import { myLanguage } from './voice.js';
import { track } from './track.js';
import { VenueCard, ZarynaMark, ZarynaFace, ZarynaSteps, plainAnswer } from './ui.jsx';
import { BookingCard, isBookingAnswer } from './zbook.jsx';

/* ⚠️ THE SAME QUESTION AGAIN IS A NEW QUESTION — Al-Noor, 12 Oct 2026: he
   spoke "wings" twice and the second time nothing happened (the server log:
   speech written down, Zaryna never asked). The answer only re-ran when the
   words CHANGED. `nonce` goes up on every ask, so asking again asks again.
   ⚠️ AND COMING BACK SHOWS THE ANSWER YOU LEFT — `keep` holds the last
   finished answer for the visit; a screen coming back to the same question
   draws it at once instead of an empty bar (and never pays for it twice). */
const KEPT = {};
export function keptQuestion(key) { return (KEPT[key] && KEPT[key].q) || ''; }

export function useZarynaAnswer(question, { market, near, source, nonce = 0, keep = null }) {
  const [ans, setAns] = useState(() => {
    const k = keep && KEPT[keep];
    return k && k.q === String(question || '').trim() ? k.ans : null;
  });   // { state, text, found, cards }
  const live = useRef(0);
  useEffect(() => {
    const q = String(question || '').trim();
    if (!q || !market) { setAns(null); return undefined; }
    if (keep && nonce === 0 && KEPT[keep] && KEPT[keep].q === q) { setAns(KEPT[keep].ans); return undefined; }
    const mine = ++live.current;
    const still = () => live.current === mine;
    setAns({ state: 'asking', text: '', found: null, cards: [] });
    track('zaryna_query', { source_screen: source || 'search' });
    const body = { message: q, market, lang: myLanguage(), ...(near ? { lat: near.lat, lng: near.lng } : {}) };
    (async () => {
      try {
        /* ⚠️ HER WORDS ARE KEPT, NOT DRAWN, UNTIL SHE HAS FINISHED — Al-Noor,
           15 Oct 2026: "why is it not clean like Google, Siri, Muse". Her steps
           show while she works; her finished answer is drawn once (the same
           rule as her own screen, Zaryna.jsx). A broken stream keeps what came. */
        let streamed = '';
        const d = await askStreaming(body,
          (piece) => { streamed += piece; },
          (found) => { if (still()) setAns((a) => ({ ...a, found })); })
          || await call('/zaryna/chat', { method: 'POST', auth: true, body });
        if (!still()) return;
        setAns((a) => {
          /* the finished answer wins over what streamed (the server may have
             asked her again, 10 Oct 2026) */
          const text = (d && d.reply) || streamed || '';
          const fin = text ? { ...a, state: 'done', text, cards: (d && d.cards) || [] } : { state: 'failed' };
          if (keep && fin.state === 'done') KEPT[keep] = { q, ans: fin };
          return fin;
        });
      } catch { if (still()) setAns({ state: 'failed' }); }
    })();
    return () => { live.current += 1; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question, market, nonce]);
  const stop = () => { live.current += 1; setAns(null); if (keep) delete KEPT[keep]; };
  return [ans, stop];
}

export function ZarynaAnswer({ question, ans, onStop, withCards = false }) {
  if (!ans || ans.state === 'failed') return null;
  const working = ans.state !== 'done';
  return (
    <div className="zanswer" aria-live="polite">
      <div className="zah">
        <span className="zah-mark">{working
          ? <ZarynaMark thinking />
          : <ZarynaFace />}</span>
        Zaryna
        {working && onStop && (
          <button type="button" className="zstop" onClick={onStop} aria-label="Stop">Stop</button>
        )}
      </div>
      {!working && ans.text && <p className="zat">{plainAnswer(ans.text)}</p>}
      {working && <p className="zat"><ZarynaSteps question={question} found={ans.found} /></p>}
      {/* ⚠️ NO CARDS WHILE SHE IS STILL THINKING — Al-Noor, 21 Oct 2026: "it
          showed a list while it's still thinking". Her steps show while she
          works; the cards come once, with her answer. */}
      {withCards && ans.state === 'done' && ans.cards && ans.cards.length > 0 && (
        <>
          <div className="zcards">
            {ans.cards.map((v) => <BookingCard key={v.id} v={v} tab={v.tab} />)}
          </div>
        </>
      )}
    </div>
  );
}
